import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ChevronRight, ExternalLink, Glasses, Plus, Receipt } from 'lucide-react';
import type { OpticalJobDetail } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Card, CardHeader } from '@/components/ui/Card';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { useSalesOrderQuery } from '@/modules/sales/api/sales.api';
import { useOrderOpticalJobsQuery } from '../api/opticalOrders.api';
import { OPTICAL_BUILDER_PERMISSIONS } from '../permissions';
import { Figure } from '../steps/ReviewStep';

const linkClass =
  'text-ink border-line hover:bg-canvas inline-flex h-10 items-center justify-center gap-2 rounded-lg border bg-white px-4 text-sm font-medium transition-colors';

/**
 * What a confirmed optical order holds: its reference, each job the server numbered, and the money.
 *
 * Read back from the server after the confirmation, never from the builder's draft. Nothing here shows a prescription's values: a job names where its prescription came from and, for a clinical one, its number. Tracking a job through the lab belongs to its own screens.
 */
export function OpticalOrderSummaryPage() {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const { canAll } = usePermissions();
  const location = useLocation();
  const { orderId = '' } = useParams<{ orderId: string }>();
  const order = useSalesOrderQuery(orderId);
  const jobs = useOrderOpticalJobsQuery(orderId);
  const justCreated = (location.state as { created?: boolean } | null)?.created === true;

  if (order.isPending || jobs.isPending) return <LoadingState />;
  if (order.isError || !order.data) {
    return <Alert tone="error">{t('opticalOrders.summary.loadError')}</Alert>;
  }

  const data = order.data;
  const jobList = jobs.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('opticalOrders.summary.title', { reference: data.reference })}
        subtitle={[data.customerName, data.branchName].filter(Boolean).join(' · ')}
        actions={
          <>
            <Link to={`/sales/orders/${data.id}`} className={linkClass}>
              <ExternalLink className="size-4" aria-hidden />
              {t('opticalOrders.summary.viewOrder')}
            </Link>
            {data.customerId && canAll(OPTICAL_BUILDER_PERMISSIONS) ? (
              <Link to={`/customers/${data.customerId}/optical-orders/new`} className={linkClass}>
                <Plus className="size-4" aria-hidden />
                {t('opticalOrders.summary.another')}
              </Link>
            ) : null}
          </>
        }
      />

      {justCreated ? (
        <Alert tone="success">
          {t('opticalOrders.summary.created', { reference: data.reference, count: jobList.length })}
        </Alert>
      ) : null}
      {jobs.isError ? <Alert tone="error">{t('opticalOrders.summary.jobsError')}</Alert> : null}

      <div className="grid gap-4 lg:grid-cols-2">
        {jobList.map((job) => (
          <JobCard key={job.id} job={job} />
        ))}
      </div>

      <Card>
        <CardHeader
          title={t('opticalOrders.summary.payment')}
          action={
            <Badge tone={data.paymentStatus === 'PAID' ? 'success' : 'warning'}>
              {t(`sales.paymentStatuses.${data.paymentStatus}`)}
            </Badge>
          }
        />
        <dl className="space-y-2 px-5 py-4">
          <Figure label={t('sales.fields.total')} value={format(data.totalMinor)} strong />
          <Figure
            label={t('sales.fields.paid')}
            value={format(data.paidMinor - data.refundedMinor)}
          />
          <Figure label={t('sales.fields.balance')} value={format(data.balanceMinor)} />
        </dl>
        {data.payments.length > 0 ? (
          <ul className="border-line space-y-1 border-t px-5 py-3 text-sm">
            {data.payments.map((payment) => (
              <li key={payment.id} className="flex justify-between gap-3">
                <span>{t(`sales.methods.${payment.method}`)}</span>
                <span dir="ltr">{format(payment.amountMinor)}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {data.invoices[0] ? (
          <div className="border-line border-t px-5 py-3">
            <Link
              to={`/sales/invoices/${data.invoices[0].id}`}
              className="text-brand-700 inline-flex items-center gap-2 text-sm font-medium"
            >
              <Receipt className="size-4" aria-hidden />
              {t('opticalOrders.summary.invoice')}{' '}
              <span className="font-mono" dir="ltr">
                {data.invoices[0].reference}
              </span>
            </Link>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

function JobCard({ job }: { job: OpticalJobDetail }) {
  const { t } = useTranslation();
  const frame = job.frame;
  const prescription = job.prescription;

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Glasses className="text-muted size-4" aria-hidden />
            <span className="font-mono" dir="ltr">
              {job.reference}
            </span>
          </span>
        }
        action={<Badge tone="brand">{t(`sales.opticalJobs.status.${job.status}`)}</Badge>}
      />
      <dl className="space-y-2 px-5 py-4 text-sm">
        <div className="flex flex-wrap gap-x-2">
          <dt className="text-muted">{t('opticalOrders.steps.frame')}:</dt>
          <dd className="text-ink">
            {frame
              ? `${frame.productName ?? ''}${frame.variantName ? ` — ${frame.variantName}` : ''}`
              : '—'}{' '}
            {frame?.sku ? (
              <span className="text-muted font-mono text-xs" dir="ltr">
                {frame.sku}
              </span>
            ) : null}
          </dd>
        </div>
        <div className="flex flex-wrap gap-x-2">
          <dt className="text-muted">{t('opticalOrders.steps.prescription')}:</dt>
          <dd className="text-ink">
            {prescription?.source === 'CLINICAL_PRESCRIPTION' ? (
              <>
                {t('opticalOrders.source.clinical')}{' '}
                <span className="font-mono" dir="ltr">
                  {prescription.clinicalPrescriptionNumber}
                </span>
              </>
            ) : (
              t('opticalOrders.source.external')
            )}
          </dd>
        </div>
        {(['right', 'left'] as const).map((eye) => {
          const lens = job.lenses[eye];
          const abbreviation = eye === 'right' ? 'OD' : 'OS';
          return (
            <div key={eye} className="flex flex-wrap gap-x-2">
              <dt className="text-muted">
                <abbr
                  dir="ltr"
                  title={t(`medical.eyes.${abbreviation}`)}
                  className="font-mono no-underline"
                >
                  {abbreviation}
                </abbr>
                :
              </dt>
              <dd className="text-ink">
                {lens
                  ? `${lens.productName}${lens.variantName ? ` — ${lens.variantName}` : ''}`
                  : '—'}
                {lens ? ` · ${t(`opticalOrders.supply.${lens.supplyMode}`)}` : ''}
              </dd>
            </div>
          );
        })}
        <div className="flex flex-wrap gap-x-2">
          <dt className="text-muted">{t('opticalOrders.lab.label')}:</dt>
          <dd className="text-ink">{job.lab ? job.lab.name : t('opticalOrders.lab.awaiting')}</dd>
        </div>
      </dl>
      <div className="border-line border-t px-5 py-3">
        <Link
          to={`/sales/optical-jobs/${job.id}`}
          aria-label={t('opticalOrders.tracking.viewJob', { reference: job.reference })}
          className="text-brand-700 focus-visible:ring-brand-200 inline-flex items-center gap-1 rounded text-sm font-medium focus-visible:ring-2 focus-visible:outline-none"
        >
          {t('opticalOrders.orderPage.track')}
          <ChevronRight className="size-4 rtl:rotate-180" aria-hidden />
        </Link>
      </div>
    </Card>
  );
}
